/**
 * Hand-loan principal / interest balances with Simple vs Compound interest
 * and payment allocation (interest-only, principal-only, or both).
 *
 * Timeline is driven only by admin-entered dates (loan start + payment paidAt).
 * We never accrue against the wall-clock "today" — shops routinely backdate entries.
 */
import { interestPerDay, interestPerMonth, type HandLoanRepaymentType } from './handLoanMath';

export type HandLoanInterestType = 'simple' | 'compound';

export type HandLoanPaymentAllocation = 'interest' | 'principal' | 'both';

export function parseHandLoanInterestType(v: unknown): HandLoanInterestType {
  return String(v ?? '').toLowerCase() === 'compound' ? 'compound' : 'simple';
}

export function handLoanInterestTypeLabel(t: HandLoanInterestType): string {
  return t === 'compound' ? 'Compound interest' : 'Simple interest';
}

export type HandLoanBalancePayment = {
  paidAt: string;
  amount: number;
  lateFeeAmount?: number;
  facilityPayoff?: boolean;
  paymentAllocation?: HandLoanPaymentAllocation | string;
  principalPortion?: number;
  interestPortion?: number;
};

export type HandLoanBalanceState = {
  principalOutstanding: number;
  interestDue: number;
  interestPaidTotal: number;
  principalPaidTotal: number;
  isPayoff: boolean;
  /** Latest admin entry date used as the ledger "as of" (ISO ms). */
  asOfMs: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTH_MS = 30 * DAY_MS;

/**
 * Receipts use the same calendar clock as the loan start: a date-only `paidAt` is local
 * noon (not UTC midnight), so a payment dated on a period boundary lands after that
 * period's interest has accrued.
 */
function paymentTime(paidAt: string): number {
  const t = ledgerDayMs(paidAt);
  return Number.isFinite(t) ? t : 0;
}

/** Parse loan start / entry dates at local noon so day counts match shop calendar days. */
function ledgerDayMs(isoOrYmd: string): number {
  const s = String(isoOrYmd || '').trim();
  if (!s) return NaN;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const t = new Date(`${s}T12:00:00`).getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/**
 * Ledger clock = loan start and admin payment dates only.
 * Never uses Date.now() — backdated entries stay consistent.
 */
function entryDateMs(entryDate?: string | null): number {
  if (!entryDate) return 0;
  return ledgerDayMs(String(entryDate).trim()) || 0;
}

export function handLoanLedgerAsOfMs(
  startDate: string,
  payments: { paidAt?: string }[],
  /** Optional: admin working / payment form date (YYYY-MM-DD or ISO). */
  entryDate?: string | null
): number {
  const startMs = ledgerDayMs(startDate);
  let asOf = Number.isFinite(startMs) ? startMs : 0;
  for (const p of payments || []) {
    const t = paymentTime(String(p.paidAt || ''));
    if (t > asOf) asOf = t;
  }
  const entryMs = entryDateMs(entryDate);
  if (entryMs > asOf) asOf = entryMs;
  return asOf;
}

function applyPayment(
  state: { principal: number; interestDue: number; interestPaid: number; principalPaid: number },
  p: HandLoanBalancePayment
): void {
  if (p.facilityPayoff) {
    state.principalPaid += state.principal;
    state.interestPaid += state.interestDue;
    state.principal = 0;
    state.interestDue = 0;
    return;
  }

  const allocation = String(p.paymentAllocation || '').toLowerCase() as HandLoanPaymentAllocation | '';
  let interestPart = Math.max(0, Math.round(Number(p.interestPortion) || 0));
  let principalPart = Math.max(0, Math.round(Number(p.principalPortion) || 0));
  const cash = Math.max(0, Math.round(Number(p.amount) || 0));

  if (allocation === 'interest') {
    interestPart = cash > 0 ? cash : interestPart;
    principalPart = 0;
  } else if (allocation === 'principal') {
    principalPart = cash > 0 ? cash : principalPart;
    interestPart = 0;
  } else if (allocation === 'both') {
    if (interestPart + principalPart <= 0 && cash > 0) {
      interestPart = Math.min(cash, state.interestDue);
      principalPart = Math.max(0, cash - interestPart);
    }
  } else if (cash > 0 && interestPart + principalPart <= 0) {
    // Legacy rows without allocation — interest first, then principal.
    interestPart = Math.min(cash, state.interestDue);
    principalPart = Math.max(0, cash - interestPart);
  }

  const payI = Math.min(interestPart, Math.max(0, state.interestDue));
  state.interestDue = Math.max(0, state.interestDue - payI);
  state.interestPaid += payI;

  // Any leftover interest cash (no due left) reduces principal.
  const leftoverInterestCash = Math.max(0, interestPart - payI);
  const payP = Math.min(principalPart + leftoverInterestCash, Math.max(0, state.principal));
  state.principal = Math.max(0, state.principal - payP);
  state.principalPaid += payP;
}

function interestBaseForPeriod(
  state: { principal: number; interestDue: number },
  inContract: boolean,
  interestType: HandLoanInterestType
): number {
  if (!inContract) return state.principal + state.interestDue;
  if (interestType === 'compound') return state.principal + state.interestDue;
  return state.principal;
}

/** Roll unpaid interest into principal (post-term compound, or in-term compound). */
function capitalizeUnpaidInterest(
  state: { principal: number; interestDue: number },
  inContract: boolean,
  interestType: HandLoanInterestType,
  periodIndex: number
): void {
  if (state.interestDue <= 0) return;
  if (!inContract) {
    state.principal += state.interestDue;
    state.interestDue = 0;
    return;
  }
  if (interestType === 'compound' && periodIndex > 0) {
    state.principal += state.interestDue;
    state.interestDue = 0;
  }
}

/**
 * Walk ledger forward: apply payments through each period, then accrue on **current**
 * outstanding principal (never the original sanction amount after partial repayments).
 * After the contract term (days or months), unpaid balances compound (interest on interest).
 */
function replayHandLoanTimeline(opts: {
  state: { principal: number; interestDue: number; interestPaid: number; principalPaid: number };
  payments: HandLoanBalancePayment[];
  startMs: number;
  asOf: number;
  periodMs: number;
  contractPeriods: number;
  interestType: HandLoanInterestType;
  rate: number;
  accrue: (base: number) => number;
}): boolean {
  let payoff = false;
  let payIdx = 0;
  const { state, payments, startMs, asOf, periodMs, contractPeriods, interestType, rate, accrue } = opts;

  const applyUntil = (untilMs: number) => {
    while (payIdx < payments.length && paymentTime(payments[payIdx].paidAt) <= untilMs) {
      const p = payments[payIdx++];
      if (p.facilityPayoff) payoff = true;
      applyPayment(state, p);
    }
  };

  let period = 0;
  while (!payoff && (state.principal > 0 || state.interestDue > 0)) {
    const periodStart = startMs + period * periodMs;
    if (periodStart > asOf) break;

    const naturalEnd = startMs + (period + 1) * periodMs - 1;
    const periodEnd = Math.min(naturalEnd, asOf);
    const periodComplete = periodEnd >= naturalEnd;
    const inContract = period < contractPeriods;

    // Apply receipts through this period first, then accrue on **current** outstanding principal.
    applyUntil(periodEnd);
    if (payoff) break;

    if (periodComplete && rate > 0 && state.principal > 0) {
      capitalizeUnpaidInterest(state, inContract, interestType, period);
      const base = interestBaseForPeriod(state, inContract, interestType);
      const increment = accrue(base);
      if (increment > 0) state.interestDue += increment;
    }

    if (periodEnd >= asOf) break;
    period += 1;
  }

  applyUntil(asOf);
  return payoff;
}

/**
 * Replay principal + pending interest using only admin entry dates.
 *
 * Short-term (daily) shop flow:
 *   - Interest accrues per completed calendar day on **outstanding principal**.
 *   - After the selected term (e.g. 365 days), overdue balances **compound** daily.
 *
 * EMI (monthly) flow:
 *   - Interest accrues each completed month on **outstanding principal** (₹60k→₹1,800;
 *     after ₹20k principal paydown on ₹40k → ₹1,200 next month).
 *   - After the EMI term ends, unpaid interest compounds monthly.
 */
export function replayHandLoanBalances(opts: {
  principal: number;
  rate: number;
  interestType: HandLoanInterestType;
  repaymentType: HandLoanRepaymentType;
  startDate: string;
  termDays?: number;
  periods?: number;
  payments: HandLoanBalancePayment[];
  /** Override ledger clock (payment form date). Defaults to last admin entry. */
  asOf?: number;
}): HandLoanBalanceState {
  const principal0 = Math.max(0, Math.round(Number(opts.principal) || 0));
  const rate = Math.max(0, Number(opts.rate) || 0);
  const interestType = opts.interestType === 'compound' ? 'compound' : 'simple';
  const startMs = ledgerDayMs(opts.startDate);
  const payments = [...(opts.payments || [])].sort((a, b) => paymentTime(a.paidAt) - paymentTime(b.paidAt));
  const asOf =
    opts.asOf != null && Number.isFinite(opts.asOf)
      ? opts.asOf
      : handLoanLedgerAsOfMs(opts.startDate, payments);

  const empty = (): HandLoanBalanceState => ({
    principalOutstanding: principal0,
    interestDue: 0,
    interestPaidTotal: 0,
    principalPaidTotal: 0,
    isPayoff: payments.some((p) => p.facilityPayoff),
    asOfMs: asOf,
  });

  if (!Number.isFinite(startMs) || principal0 <= 0) return empty();

  const timelineState = {
    principal: principal0,
    interestDue: 0,
    interestPaid: 0,
    principalPaid: 0,
  };

  const contractPeriods =
    opts.repaymentType === 'short_term'
      ? Math.max(1, Math.round(Number(opts.termDays) || 30))
      : Math.max(1, Math.floor(Number(opts.periods) || 1));

  const periodMs = opts.repaymentType === 'short_term' ? DAY_MS : MONTH_MS;
  const accrue =
    opts.repaymentType === 'short_term'
      ? (base: number) => interestPerDay(base, rate)
      : (base: number) => interestPerMonth(base, rate);

  const payoff = replayHandLoanTimeline({
    state: timelineState,
    payments,
    startMs,
    asOf,
    periodMs,
    contractPeriods,
    interestType,
    rate,
    accrue,
  });

  return {
    principalOutstanding: Math.max(0, Math.round(timelineState.principal)),
    interestDue: Math.max(0, Math.round(timelineState.interestDue)),
    interestPaidTotal: Math.max(0, Math.round(timelineState.interestPaid)),
    principalPaidTotal: Math.max(0, Math.round(timelineState.principalPaid)),
    isPayoff: payoff,
    asOfMs: asOf,
  };
}

/** Validate operator-entered allocation against cash received. */
export function validateHandLoanPaymentAllocation(input: {
  allocation: HandLoanPaymentAllocation;
  totalAmount: number;
  interestAmount?: number;
  principalAmount?: number;
  interestDue: number;
  principalOutstanding: number;
}): { ok: true; interestPortion: number; principalPortion: number } | { ok: false; error: string } {
  const total = Math.max(0, Math.round(Number(input.totalAmount) || 0));
  if (total <= 0) return { ok: false, error: 'Enter the amount received.' };

  if (input.allocation === 'interest') {
    if (input.interestDue <= 0) {
      return { ok: false, error: 'No interest is due on this loan right now.' };
    }
    if (total > input.interestDue + 0.5) {
      return {
        ok: false,
        error: `Interest payment (₹${total.toLocaleString('en-IN')}) cannot exceed interest due (₹${input.interestDue.toLocaleString('en-IN')}).`,
      };
    }
    return { ok: true, interestPortion: total, principalPortion: 0 };
  }

  if (input.allocation === 'principal') {
    if (input.principalOutstanding <= 0) {
      return { ok: false, error: 'No outstanding principal left on this loan.' };
    }
    if (total > input.principalOutstanding + 0.5) {
      return {
        ok: false,
        error: `Principal left is ₹${input.principalOutstanding.toLocaleString('en-IN')} (not the original loan amount). Enter ₹${input.principalOutstanding.toLocaleString('en-IN')}, or use Full settlement to close principal + interest.`,
      };
    }
    return { ok: true, interestPortion: 0, principalPortion: total };
  }

  const interestAmount = Math.max(0, Math.round(Number(input.interestAmount) || 0));
  const principalAmount = Math.max(0, Math.round(Number(input.principalAmount) || 0));
  if (interestAmount + principalAmount !== total) {
    return {
      ok: false,
      error: `Interest (₹${interestAmount.toLocaleString('en-IN')}) + Principal (₹${principalAmount.toLocaleString('en-IN')}) must equal total payment (₹${total.toLocaleString('en-IN')}).`,
    };
  }
  if (interestAmount > input.interestDue + 0.5) {
    return {
      ok: false,
      error: `Interest amount cannot exceed interest due (₹${input.interestDue.toLocaleString('en-IN')}).`,
    };
  }
  if (principalAmount > input.principalOutstanding + 0.5) {
    return {
      ok: false,
      error: `Principal left is ₹${input.principalOutstanding.toLocaleString('en-IN')}. Reduce principal amount (₹${(principalAmount - input.principalOutstanding).toLocaleString('en-IN')} already collected toward principal), or use Full settlement.`,
    };
  }
  return { ok: true, interestPortion: interestAmount, principalPortion: principalAmount };
}
